import Moment from 'moment';
import {useEffect, useMemo, useRef, useState} from 'react';
import {graphql} from 'react-relay';

import {isWeekDeclined} from '@/components/timesheets/time-lock/DeclinedWeekLockUtil';
import {useForecastFetchQuery} from '@/forecast-app/shared/hooks/useForecastFetchQuery';
import {usingInternalTimeApproval} from '@/forecast-app/shared/util/FeatureUtil';

/**
 * Week-scoped approval data for one person: the rows in the week, and the per-week approval row
 * that only exists for weeks submitted with no hours on them. Both arms are needed to answer
 * whether the week is declined — see `isWeekDeclined`.
 *
 * The same two selections appear twice, aliased: once for the week being asked about, once for the
 * window of weeks the submit lock covers. One round trip answers both questions, and the window
 * half is skipped entirely when there is no submit lock to look behind.
 */
export const TimeRegistrationModalDeclinedWeekQuery = graphql`
	query useIsPersonWeekDeclined_Query(
		$personId: ID
		$weekStart: String
		$weekEnd: String
		$windowStart: String
		$windowEnd: String
		$hasWindow: Boolean!
	) {
		viewer {
			id
			component(name: "time_registration_modal_declined_week")
			company {
				id
				useInternalTimeApproval
				person(id: $personId) {
					id
					weekApprovals(startDate: $weekStart, endDate: $weekEnd) {
						weekStartYear
						weekStartMonth
						weekStartDay
						approvalStatus
					}
					timeRegistrations(first: 10000, startDate: $weekStart, endDate: $weekEnd) {
						edges {
							node {
								id
								year
								month
								day
								approvalStatus
								idleTime {
									id
									isInternalTime
								}
							}
						}
					}
					windowApprovals: weekApprovals(startDate: $windowStart, endDate: $windowEnd) @include(if: $hasWindow) {
						weekStartYear
						weekStartMonth
						weekStartDay
						approvalStatus
					}
					windowTimeRegistrations: timeRegistrations(first: 10000, startDate: $windowStart, endDate: $windowEnd)
						@include(if: $hasWindow) {
						edges {
							node {
								id
								year
								month
								day
								approvalStatus
								idleTime {
									id
									isInternalTime
								}
							}
						}
					}
				}
			}
		}
	}
`;

/**
 * How far behind the submit lock to look for a week that was sent back.
 *
 * The lock is a single date advanced by *max* on every submit, so it sits on the most recently
 * submitted week, not necessarily on the declined one: submit two weeks and have the first
 * declined, and the decline is a week or more below the lock. Looking only at the lock's own week
 * would miss it, and the picker's floor would keep it out of reach. Bounded rather than open-ended
 * because the query has to be: eight weeks covers a monthly approval cycle and the latency around
 * it, and a decline older than that is still reachable from My Timesheets, which seeds the day the
 * owner clicked.
 */
const LOCK_WINDOW_WEEKS = 8;

export type PersonWeekDeclinedAnswer = {
	/** Whether the week containing the date asked about has been sent back. */
	declined: boolean;
	/** Whether the answer for that week has landed — see below; a failed request counts. */
	answered: boolean;
	/**
	 * Every week start (`YYYY-MM-DD`) known to be declined: the weeks in the window behind the
	 * submit lock, plus the week asked about when it is declined. What a caller needs to tell
	 * "inside the lock and still locked" from "inside the lock but sent back".
	 */
	declinedWeekStarts: string[];
};

/** The start of the week `date` falls in, as `YYYY-MM-DD` — the boundary this hook asks about. */
export const weekStartKeyOf = (date: Date): string => Moment(date).startOf('week').format('YYYY-MM-DD');

/**
 * The starts of the weeks to ask about behind `submissionLockDate`, oldest first, ending with the
 * week the lock itself falls in. See `LOCK_WINDOW_WEEKS` for why it is a window and not just that
 * one week.
 */
export const lockWindowWeekStarts = (submissionLockDate: Date): Moment.Moment[] => {
	const lockWeekStart = Moment(submissionLockDate).startOf('week');
	const first = lockWeekStart.clone().subtract((LOCK_WINDOW_WEEKS - 1) * 7, 'day');
	return Array.from({length: LOCK_WINDOW_WEEKS}, (_unused, index) => first.clone().add(index * 7, 'day'));
};

/**
 * Whether the person's own submission lock still holds `date`, given the weeks known to have been
 * sent back.
 *
 * For a picker whose floor has been relaxed to the company lock: everything on or before the
 * submission lock is still held, *except* the days of a week that was sent back. Dates after the
 * lock were never held by it. A week nothing is known about counts as held — the same direction
 * the gate errs in, and the state a still-in-flight answer is in.
 *
 * The one case this cannot express is a day inside a declined week that holds approved or invoiced
 * time: that veto is per row and lives on the server.
 */
export const isDateHeldBySubmissionLock = (
	date: {year: number; month: number; day: number},
	submissionLockDate: Date | null | undefined,
	declinedWeekStarts: string[]
): boolean => {
	if (!submissionLockDate) return false;
	const candidate = Moment({year: date.year, month: date.month - 1, day: date.day});
	if (candidate.isAfter(Moment(submissionLockDate), 'day')) return false;
	return !declinedWeekStarts.includes(candidate.clone().startOf('week').format('YYYY-MM-DD'));
};

/**
 * Whether the approver has sent back the week containing `selectedDate`, for `personId`.
 *
 * Fetched rather than read from the modal's own fragment because the answer is week-scoped while
 * the selected date is client state: the fragment carries the person's submit-lock date but
 * nothing about which week was declined. Re-fetches only when the *week* changes, so stepping
 * between days inside one week costs nothing.
 *
 * `submissionLockDate` is what the caller is trying to look behind, and it is optional: pass it to
 * get `declinedWeekStarts` for the weeks the lock covers, omit it to ask about the selected week
 * alone. It only changes when the person does, so the window half of the answer is stable while
 * the user moves around the picker.
 *
 * `declined` is `false` until the answer is known — in flight and on failure — so the lock stays
 * as strict as it was rather than briefly permitting a date the server would refuse. `answered` is
 * what lets a caller tell "not declined" apart from "not yet known": anything that *moves* a date
 * in response to the lock has to wait for it, or it acts on the in-flight `false` and lands
 * somewhere the real answer would not have sent it.
 *
 * `answered` is true only once the response for the week currently being asked about has landed,
 * so a slow reply for a week the user has since left cannot be mistaken for this week's answer. A
 * failed request counts as landed — `declined` stays false, but a caller waiting for the answer
 * before it moves a date must not wait forever and end up applying no lock at all.
 *
 * The week boundary comes from Moment, not date-fns, even though this tree otherwise uses
 * date-fns: Moment follows the configured locale's first day of week, which is the week the
 * timesheet grid and the backend's approval scopes both use. `startOfWeek` from date-fns defaults
 * to Sunday regardless, which would put this modal on a different week from every other surface
 * wherever the week starts on a Monday.
 */
export function useIsPersonWeekDeclined(
	personId: string | null | undefined,
	selectedDate: Date | null,
	submissionLockDate?: Date | null
): PersonWeekDeclinedAnswer {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const {data, fetch} = useForecastFetchQuery<any, Record<string, unknown>>(TimeRegistrationModalDeclinedWeekQuery);

	const week = useMemo(() => {
		if (!selectedDate) return null;
		const start = Moment(selectedDate).startOf('week');
		return {
			year: start.year(),
			month: start.month() + 1,
			day: start.date(),
			startString: start.format('YYYY-MM-DD'),
			endString: start.clone().endOf('week').format('YYYY-MM-DD'),
		};
	}, [selectedDate]);

	// The weeks behind the submit lock, ending with the lock's own week.
	const lockWindow = useMemo(() => {
		if (!submissionLockDate) return null;
		const weekStarts = lockWindowWeekStarts(submissionLockDate);
		return {
			startString: weekStarts[0].format('YYYY-MM-DD'),
			endString: weekStarts[weekStarts.length - 1].clone().endOf('week').format('YYYY-MM-DD'),
			weekStarts,
		};
	}, [submissionLockDate]);

	// Which question the in-flight request is for, and which one we have actually heard back about.
	// Without this a reply for an earlier week would read as the current week's answer, because
	// the underlying hook just overwrites `data` whenever a response lands.
	const requestedWeek = useRef<string | null>(null);
	const [answeredWeek, setAnsweredWeek] = useState<string | null>(null);
	const asking = `${personId}:${week?.startString}:${lockWindow?.startString ?? '-'}`;

	useEffect(() => {
		if (!personId || !week) return;
		requestedWeek.current = asking;
		// Answered on failure too, not only on success. `declined` stays false either way, but a
		// caller that waits for the answer before moving a date would otherwise wait forever on a
		// failed request and act on no lock at all — less strict than the lock it was waiting for.
		const answer = () => {
			if (requestedWeek.current === asking) setAnsweredWeek(asking);
		};
		fetch(
			{
				personId,
				weekStart: week.startString,
				weekEnd: week.endString,
				windowStart: lockWindow?.startString ?? week.startString,
				windowEnd: lockWindow?.endString ?? week.endString,
				hasWindow: !!lockWindow,
			},
			answer,
			answer
		);
		// `fetch` is a fresh closure every render, so depending on it would refetch in a loop.
	}, [personId, asking]);

	const answered = !personId || !week || answeredWeek === asking;

	const internalTimeApproved = usingInternalTimeApproval(data?.viewer?.company?.useInternalTimeApproval);

	const declined = useMemo(() => {
		const person = data?.viewer?.company?.person;
		if (!person || !week || !answered) return false;

		return isWeekDeclined(
			{year: week.year, month: week.month, day: week.day},
			person.timeRegistrations?.edges,
			person.weekApprovals,
			// Internal time counts where the company approves it, matching the server's gate; time
			// off never does.
			internalTimeApproved
		);
	}, [data, answered, week?.startString, internalTimeApproved]);

	const declinedWeekStarts = useMemo(() => {
		const person = data?.viewer?.company?.person;
		const starts: string[] = [];

		if (person && lockWindow) {
			for (const weekStart of lockWindow.weekStarts) {
				const isDeclined = isWeekDeclined(
					{year: weekStart.year(), month: weekStart.month() + 1, day: weekStart.date()},
					person.windowTimeRegistrations?.edges,
					person.windowApprovals,
					internalTimeApproved
				);
				if (isDeclined) starts.push(weekStart.format('YYYY-MM-DD'));
			}
		}

		// The week asked about is answered by its own selection, which may sit outside the window.
		if (declined && week && !starts.includes(week.startString)) starts.push(week.startString);

		return starts;
	}, [data, lockWindow?.startString, internalTimeApproved, declined, week?.startString]);

	return {declined, answered, declinedWeekStarts};
}
